import { Observable } from 'rxjs';
import { PagedList } from '../data/models/paged-list.model';
import {
  Passenger,
  PassengerAddress,
  PassengerPreference,
} from '../data/models/passenger.model';

import httpService from './httpService';

class PassengerService {
  private readonly apiBase;
  private readonly apiTransportBase;

  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_PASSENGER_API_URL.trim();
    this.apiTransportBase =
      __environment__.REACT_APP_TRANSPORT_PASSENGER_API_URL.trim();
  }

  private createParam(name: string, value: string | null) {
    if (value) {
      return `&${name}=${encodeURIComponent(value)}`;
    }
    return '';
  }

  public getPassengers(
    clientBC: string,
    searchText: string,
    page: number,
    pageSize: number,
  ): Observable<PagedList<Passenger>> {
    const params = [
      `page=${page}&pageSize=${pageSize}`,
      this.createParam('clientBC', clientBC),
      this.createParam('searchText', searchText),
    ].filter((p) => !!p);
    const url = `${this.apiBase}?${params.join('')}`;
    return httpService.getJSON(url);
  }

  public getPassenger(id: string, signal?: AbortSignal): Observable<Passenger> {
    const url = `${
      this.apiBase
    }/getPassenger?passengerUUID=${encodeURIComponent(id)}`;
    return httpService.getJSON(url, signal);
  }

  public getPassengerPreferences(
    id: string,
    clientBC?: string,
  ): Observable<PassengerPreference[]> {
    const url = `${this.apiTransportBase}/${encodeURIComponent(id)}/preferences${clientBC ? `?clientBC=${encodeURIComponent(clientBC)}` : ''}`;
    return httpService.getJSON(url);
  }

  public savePassengerPreferences(
    id: string,
    prefs: PassengerPreference[],
  ): Observable<PassengerPreference[]> {
    const url = `${this.apiTransportBase}/${encodeURIComponent(id)}/preferences`;
    return httpService.putJSON(url, prefs);
  }

  public getAllPassengers(
    page: number,
    pageSize: number,
  ): Observable<PagedList<Passenger>> {
    const params = [`page=${page}&pageSize=${pageSize}`];
    const url = `${this.apiBase}?${params.join('')}`;
    return httpService.getJSON(url);
  }

  public createPassenger(passenger: Passenger): Observable<Passenger> {
    const url = `${this.apiBase}/createPassenger`;
    return httpService.postJSON(url, passenger);
  }

  public updatePassenger(passenger: Passenger): Observable<Passenger> {
    const url = `${this.apiBase}/updatePassenger`;
    return httpService.postJSON(url, passenger);
  }

  public createPassengerAddress(
    passengerUUID: string,
    address: PassengerAddress,
    passengerAddressUseBC: string,
    label: string,
  ) {
    const url = `${this.apiBase}/addReservationAddress`;
    const data = {
      passengerUUID,
      address: {
        ...address,
        passengerAddressUseBC,
        label,
      },
    };
    return httpService.postJSON(url, data);
  }

  public deletePassengerAddress(passengerUUID: string, label: string) {
    const url = `${this.apiBase}/removeReservationAddress`;
    const data = {
      passengerUUID,
      address: {
        label,
        passengerAddressUseBC: 'RESERVATION',
      },
    };
    return httpService.postJSON(url, data);
  }
}

const passengerService = new PassengerService();

export default passengerService;
